import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { OIDC_ENABLED } from '../config/oidcConfig';
import { User } from '../entities/User';
import { userService } from '../services/userService';
import { msalService } from '../services/msalService';
import { oidcService } from '../services/oidcService';
import { authProviderService, AuthProviderType } from '../services/authProviderService';
import { demoService } from '../services/demoService';
import { AuthContextType } from '../types/components';
import { authLogger } from '../utils/logger';
import { MoodSetupModal } from '../components/user/MoodSetupModal';
import { isAuthorizationError } from '../types/errors';
import { isNativeApp } from '../utils/nativePlatform';
import { recordNativeDiagnostic } from '../utils/nativeDiagnostics';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const msalAccount = useAccount(accounts[0] || {}) as AccountInfo | null;
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [backendError, setBackendError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [oidcInitialized, setOidcInitialized] = useState(false);
  const [showMoodSetup, setShowMoodSetup] = useState(false);
  const [moodSetupCompleted, setMoodSetupCompleted] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<any | null>(null);
  const [authProvider, setAuthProvider] = useState<AuthProviderType | null>(null);
  const [isOidcAuthenticated, setIsOidcAuthenticated] = useState(false);
  const [oidcAccount, setOidcAccount] = useState<any>(null);
  
  // Demo mode state
  const [isDemoMode, setIsDemoMode] = useState(() => demoService.isDemoModeActive());
  const [needsDemoPassword, setNeedsDemoPassword] = useState(false);
  const isDemoModeAvailable = demoService.isDemoModeEnabled();
  const [urlDemoProcessed, setUrlDemoProcessed] = useState(false);

  // Check URL for demo password parameter on mount (auto demo login via URL)
  useEffect(() => {
    // Only process once and only if demo mode is available
    if (urlDemoProcessed || !isDemoModeAvailable) {
      return;
    }

    // Don't process if already authenticated via MSAL/OIDC
    if (isMsalAuthenticated || isOidcAuthenticated) {
      setUrlDemoProcessed(true);
      return;
    }

    // Don't process if already in demo mode
    if (demoService.isDemoModeActive()) {
      setUrlDemoProcessed(true);
      return;
    }

    const processUrlDemoParam = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const demoPassword = urlParams.get('demo');

      if (!demoPassword) {
        setUrlDemoProcessed(true);
        return;
      }

      authLogger.info('Demo password found in URL, validating...');

      // Remove the demo parameter from URL immediately (security: prevent password in browser history)
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('demo');
      window.history.replaceState({}, '', newUrl.toString());

      try {
        const isValid = await demoService.validateDemoPassword(demoPassword);

        if (isValid) {
          authLogger.info('URL demo password validated, activating demo mode');
          demoService.setDemoPassword(demoPassword);
          demoService.activateDemoMode();
          setIsDemoMode(true);
          setAuthError(null);
          setBackendError(null);
          navigate('/chat');
        } else {
          authLogger.warn('URL demo password is invalid');
          setAuthError('Invalid demo password');
        }
      } catch (error) {
        authLogger.error('Failed to validate URL demo password:', error);
        setAuthError('Failed to validate demo password');
      }

      setUrlDemoProcessed(true);
    };

    processUrlDemoParam();
  }, [isDemoModeAvailable, isMsalAuthenticated, isOidcAuthenticated, urlDemoProcessed, navigate]);

  // SECURITY: If user is authenticated via MSAL/OIDC, ensure demo mode is disabled
  // This prevents accidental demo mode usage when real auth is active
  useEffect(() => {
    if ((isMsalAuthenticated || isOidcAuthenticated) && demoService.isDemoModeActive()) {
      authLogger.warn('Real authentication detected while demo mode active - disabling demo mode');
      demoService.deactivateDemoMode(false);
      setIsDemoMode(false);
    }
  }, [isMsalAuthenticated, isOidcAuthenticated]);

  // Determine if user is authenticated (from either provider or demo mode)
  // Demo mode is mutually exclusive with real authentication
  const isAuthenticated = isMsalAuthenticated || isOidcAuthenticated || isDemoMode;
  const account = authProvider === 'oidc' ? oidcAccount : msalAccount;
  const pendingAdminReturnUrl = typeof window !== 'undefined'
    ? localStorage.getItem('adminReturnUrl')
    : null;
  const isAdminSignInFlow = location.pathname.startsWith('/admin') || (pendingAdminReturnUrl?.startsWith('/admin') ?? false);

  // Track MSAL initialization status
  useEffect(() => {
    const checkMsalInitialization = (): boolean => {
      const initialized = !!window.__MSAL_INITIALIZED__;
      authLogger.debug('MSAL initialization status:', initialized);
      setMsalInitialized(initialized);
      return initialized;
    };

    if (checkMsalInitialization()) {
      return;
    }

    const handleMsalInitialized = (): void => {
      authLogger.debug('Received MSAL initialization event');
      setMsalInitialized(true);
    };

    window.addEventListener('msalInitialized', handleMsalInitialized);

    const interval = setInterval(() => {
      if (checkMsalInitialization()) {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      window.removeEventListener('msalInitialized', handleMsalInitialized);
      clearInterval(interval);
    };
  }, []);

  // Track OIDC initialization status
  useEffect(() => {
    if (!OIDC_ENABLED) {
      authLogger.debug('OIDC is disabled, skipping initialization tracking');
      return;
    }

    const checkOidcInitialization = (): boolean => {
      const initialized = !!window.__OIDC_INITIALIZED__;
      authLogger.debug('OIDC initialization status:', initialized);
      setOidcInitialized(initialized);
      return initialized;
    };

    if (checkOidcInitialization()) {
      // Check OIDC auth state
      checkOidcAuthState();
      return;
    }

    const handleOidcInitialized = (): void => {
      authLogger.debug('Received OIDC initialization event');
      setOidcInitialized(true);
      checkOidcAuthState();
    };

    window.addEventListener('oidcInitialized', handleOidcInitialized);

    const interval = setInterval(() => {
      if (checkOidcInitialization()) {
        clearInterval(interval);
        checkOidcAuthState();
      }
    }, 100);

    return () => {
      window.removeEventListener('oidcInitialized', handleOidcInitialized);
      clearInterval(interval);
    };
  }, []);

  // Check OIDC authentication state
  const checkOidcAuthState = async (): Promise<void> => {
    if (!OIDC_ENABLED) return;

    try {
      const isAuth = await oidcService.isAuthenticated();
      if (isAuth) {
        authLogger.debug('OIDC user is authenticated');
        setIsOidcAuthenticated(true);
        setAuthProvider('oidc');
        authProviderService.setActiveProvider('oidc');
        const profile = oidcService.getUserProfile();
        setOidcAccount(profile);
      }
    } catch (error) {
      authLogger.error('Failed to check OIDC auth state:', error);
    }
  };

  // Listen for OIDC auth events
  useEffect(() => {
    if (!OIDC_ENABLED) return;

    const handleUserLoaded = (): void => {
      authLogger.debug('OIDC user loaded event');
      recordNativeDiagnostic('AuthContext oidcUserLoaded received');
      setIsOidcAuthenticated(true);
      setAuthProvider('oidc');
      authProviderService.setActiveProvider('oidc');
      const profile = oidcService.getUserProfile();
      setOidcAccount(profile);
    };

    const handleUserUnloaded = (): void => {
      authLogger.debug('OIDC user unloaded event');
      if (authProvider === 'oidc') {
        setIsOidcAuthenticated(false);
        setOidcAccount(null);
      }
    };

    const handleUserSignedOut = (): void => {
      authLogger.debug('OIDC user signed out event');
      if (authProvider === 'oidc') {
        setIsOidcAuthenticated(false);
        setOidcAccount(null);
        setUser(null);
      }
    };

    const handleInitError = (event: Event): void => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      const message = detail?.message || 'OIDC initialization failed';
      authLogger.error('OIDC init error event:', message);
      // Surface so the login UI stops showing the processing spinner and shows the reason.
      setAuthError(`Sign in with Renewmyday failed: ${message}`);
      setError(`Sign in with Renewmyday failed: ${message}`);
    };

    window.addEventListener('oidcUserLoaded', handleUserLoaded);
    window.addEventListener('oidcUserUnloaded', handleUserUnloaded);
    window.addEventListener('oidcUserSignedOut', handleUserSignedOut);
    window.addEventListener('oidcInitError', handleInitError);

    return () => {
      window.removeEventListener('oidcUserLoaded', handleUserLoaded);
      window.removeEventListener('oidcUserUnloaded', handleUserUnloaded);
      window.removeEventListener('oidcUserSignedOut', handleUserSignedOut);
      window.removeEventListener('oidcInitError', handleInitError);
    };
  }, [authProvider]);

  // Determine active provider on mount
  useEffect(() => {
    const storedProvider = sessionStorage.getItem('auth_provider') as AuthProviderType | null;
    if (storedProvider) {
      setAuthProvider(storedProvider);
      authLogger.debug('Restored auth provider from session:', storedProvider);
    } else if (isMsalAuthenticated) {
      setAuthProvider('msal');
      authProviderService.setActiveProvider('msal');
    }
  }, [isMsalAuthenticated]);

  // Initialize demo mode user when in demo mode
  useEffect(() => {
    if (!isDemoMode) {
      return;
    }

    const initializeDemoUser = async () => {
      authLogger.debug('Demo mode is active, initializing demo user');
      setIsLoading(true);
      
      const demoUserInfo = demoService.getDemoUserInfo();
      
      try {
        // Try to get existing profile from backend first
        const existingProfile = await userService.getCurrentUser();
        
        if (existingProfile) {
          authLogger.info('Demo user profile exists in backend:', { id: existingProfile.id });
          const demoUser = new User({
            ...existingProfile,
            id: demoUserInfo.id, // Ensure we use the demo user ID
          });
          setUser(demoUser);
          setIsLoading(false);
          return;
        }
      } catch (error: any) {
        // Profile not found - this is expected for new demo users
        authLogger.debug('Demo user profile not found, will create one');
      }

      // Create profile in backend for new demo users
      try {
        const profileData = {
          displayName: demoUserInfo.displayName,
          email: demoUserInfo.email,
          status: 'active' as const,
          language: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          roles: demoUserInfo.roles,
          chatPreferences: {
            theme: 'light' as const,
            fontSize: 'medium' as const,
            enterToSend: true,
            soundEnabled: true,
            notificationsEnabled: true
          },
          metadata: {
            onboardingCompleted: true, // Skip onboarding for demo users
            privacyNoticeReadConsent: true,
            dataProcessingConsent: true,
            privacyConsent: true,
            useNickname: false
          }
        };

        authLogger.info('Creating demo user profile in backend...');
        const createdProfile = await userService.createProfile(profileData);
        authLogger.info('Demo user profile created successfully:', { id: createdProfile.id });

        const demoUser = new User({
          ...createdProfile,
          id: demoUserInfo.id, // Ensure we use the demo user ID
        });
        setUser(demoUser);
      } catch (createError) {
        authLogger.error('Failed to create demo user profile:', createError);
        
        // Fall back to local-only user if backend creation fails
        const demoUser = new User({
          id: demoUserInfo.id,
          displayName: demoUserInfo.displayName,
          email: demoUserInfo.email,
          roles: demoUserInfo.roles,
          status: 'active',
          metadata: {
            onboardingCompleted: true,
            privacyNoticeReadConsent: true,
            dataProcessingConsent: true,
            privacyConsent: true,
            useNickname: false
          }
        });
        setUser(demoUser);
        authLogger.warn('Using local-only demo user (backend unavailable)');
      } finally {
        setIsLoading(false);
      }
    };

    initializeDemoUser();
  }, [isDemoMode]);

  // Initialize user profile when authenticated
  useEffect(() => {
    const initializeUser = async (): Promise<void> => {
      // Skip user initialization for demo mode - handled separately
      if (isDemoMode) {
        authLogger.debug('Skipping user initialization - demo mode active');
        return;
      }

      authLogger.debug('User initialization effect:', {
        isAuthenticated,
        msalInitialized,
        oidcInitialized,
        authProvider,
        account: account ? { localAccountId: account.localAccountId, username: account.username } : null,
        user: user ? { id: user.id } : null,
        accountsLength: accounts.length
      });

      // Don't initialize user if auth provider isn't ready
      const providerReady = (authProvider === 'msal' && msalInitialized) || 
                           (authProvider === 'oidc' && oidcInitialized) ||
                           (msalInitialized && !authProvider); // Default to MSAL if no provider set
      
      if (!providerReady) {
        authLogger.debug('Auth provider not initialized yet, waiting...');
        return;
      }

      if (isAuthenticated && account && !user) {
        authLogger.debug('Starting user profile load for account:', account.username || account.email);
        authLogger.debug('Backend API URL:', process.env.REACT_APP_BACKEND_API_URL || 'https://backend-wrong.coint.ai');
        authLogger.debug('Auth provider:', authProvider);
        setIsLoading(true);
        try {
          const userProfile = await userService.getCurrentUser();
          authLogger.debug('User profile loaded:', userProfile ? 'success' : 'empty');
          if (userProfile) {
            // Merge account data with backend user data
            const mergedUser = User.fromAuthContext(account, userProfile);
            authLogger.debug('Merged user created:', { id: mergedUser.id, onboardingCompleted: mergedUser.hasCompletedOnboarding() });
            setUser(mergedUser);
            authLogger.debug('User state updated - this should trigger navigation effect');
            
            setTimeout(() => {
              authLogger.debug('Post-user-set check:', {
                userSet: !!mergedUser,
                onboardingCompleted: mergedUser.hasCompletedOnboarding(),
                shouldTriggerMoodCheck: mergedUser.hasCompletedOnboarding()
              });
            }, 100);
          } else {
            authLogger.warn('No user profile returned from backend, auto-creating profile');
            // Auto-create profile in backend when it doesn't exist
            const profileData = {
              displayName: account.name || account.username || 'User',
              email: account.username || account.email,
              status: 'active' as const,
              language: 'en',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
              roles: ['user'],
              chatPreferences: {
                theme: 'light' as const,
                fontSize: 'medium' as const,
                enterToSend: true,
                soundEnabled: true,
                notificationsEnabled: true
              },
              metadata: {
                onboardingCompleted: false, // Force onboarding for new users
                privacyNoticeReadConsent: false,
                dataProcessingConsent: false,
                privacyConsent: false,
                useNickname: false
              }
            };
            
            try {
              authLogger.info('Creating new user profile in backend...');
              const createdUser = await userService.createProfile(profileData);
              authLogger.info('User profile created successfully:', { id: createdUser.id });
              
              // Merge with account data
              const mergedUser = User.fromAuthContext(account, createdUser);
              setUser(mergedUser);
              authLogger.debug('User state updated with newly created profile');
            } catch (createError) {
              authLogger.error('Failed to auto-create user profile:', createError);
              // Fall back to in-memory user if creation fails
              const defaultUser = User.fromAuthContext(account, {
                id: account.localAccountId,
                displayName: account.name || account.username,
                email: account.username,
                metadata: {
                  onboardingCompleted: false,
                  privacyNoticeReadConsent: false,
                  dataProcessingConsent: false,
                  privacyConsent: false,
                  useNickname: false
                }
              });
              authLogger.debug('Falling back to in-memory default user:', { id: defaultUser.id });
              setUser(defaultUser);
            }
          }
        } catch (error) {
          authLogger.error('Failed to load user profile:', error);
          
          // Check if this is a token acquisition error
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isTokenError = errorMessage.includes('Unable to acquire access token') ||
                              errorMessage.includes('interaction_required') ||
                              errorMessage.includes('login_required');
          
          if (isTokenError) {
            authLogger.warn('Token acquisition failed - user likely has expired cached credentials');
            authLogger.warn('Triggering force reauthentication to get fresh token...');
            // Don't set isLoading to false here - let the re-auth flow handle it
            await msalService.forceReauthenticationPublic();
            return; // Exit early as we're forcing reauthentication
          }
          
          // ====== Check for authorization errors ======
          if (isAuthorizationError(error)) {
            authLogger.warn('Authorization error detected:', {
              type: error.type,
              status: error.status
            });
            
            setAuthorizationError(error);
            // Keep MSAL account info so we can show who they're signed in as
            // But don't set user profile
            setIsLoading(false);
            return;
          }
          
          // Check if this is a critical auth error that requires force reauthentication
          if (msalService.shouldForceReauthentication(error)) {
            authLogger.warn('Critical auth error detected during user profile loading, forcing reauthentication...');
            await msalService.forceReauthenticationPublic();
            return; // Exit early as we're forcing reauthentication
          }
          
          setBackendError('Failed to load user profile');
        } finally {
          setIsLoading(false);
        }
      } else if (!isAuthenticated && !isDemoMode) {
        if (user) {
          authLogger.debug('User was authenticated but now is not, clearing user state');
        }
        setUser(null);
        setIsLoading(false);
      } else if (isAuthenticated && !account && accounts.length === 0 && !isDemoMode) {
        authLogger.warn('Authenticated but no account available and no accounts in array');
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [isAuthenticated, account, user, accounts.length, msalInitialized, oidcInitialized, authProvider, isDemoMode]);

  // Separate effect to handle mood setup after user is loaded
  useEffect(() => {
    // Skip if no user
    if (!user) {
      return;
    }

    // Admin routes handle their own access checks and should not trigger the wellbeing check-in.
    if (isAdminSignInFlow) {
      authLogger.debug('Admin route/sign-in flow detected - skipping mood check');
      return;
    }

    // Skip if there's a backend error or authorization error (backend unavailable)
    if (backendError || authorizationError) {
      authLogger.debug('Backend/authorization error detected - skipping mood check');
      return;
    }

    // Skip if we're already showing the mood setup
    if (showMoodSetup) {
      return;
    }

    // Skip if we've already completed the mood setup check for this user
    if (moodSetupCompleted) {
      return;
    }

    // Check if onboarding is required for this user
    // If user has consent config, onboarding is required - wait for it to complete
    // If user has no consent config, onboarding is skipped - proceed with mood check
    const isOnboardingRequired = !!user.consent;
    const hasCompletedOnboarding = user.hasCompletedOnboarding();
    
    if (isOnboardingRequired && !hasCompletedOnboarding) {
      authLogger.debug('Onboarding required but not completed - skipping mood check for now', {
        userId: user.id,
        hasConsent: !!user.consent
      });
      return;
    }

    authLogger.debug('User changed - checking mood setup requirement:', {
      userId: user.id,
      isOnboardingRequired,
      hasCompletedOnboarding,
      moodSetupCompleted,
      showMoodSetup
    });

    const checkMoodSetup = async () => {
      try {
        authLogger.debug('Checking if mood is stale for user:', user.id);
        const isMoodStale = await userService.isMoodStale();
        authLogger.debug('Mood staleness check result:', { isMoodStale, userId: user.id });
        
        if (isMoodStale) {
          authLogger.debug('Mood is stale - showing mood setup modal');
          setShowMoodSetup(true);
        } else {
          authLogger.debug('Mood is recent - marking mood setup as completed');
          setMoodSetupCompleted(true);
        }
      } catch (error) {
        authLogger.warn('Failed to check mood status, skipping mood setup:', error);
        setMoodSetupCompleted(true);
      }
    };

    checkMoodSetup();
  }, [user, moodSetupCompleted, showMoodSetup, backendError, authorizationError, isAdminSignInFlow]);

  // Close mood modal if backend error, authorization error, or admin navigation occurs
  useEffect(() => {
    if ((backendError || authorizationError || isAdminSignInFlow) && showMoodSetup) {
      authLogger.debug('Closing mood setup modal due to backend/authorization error or admin navigation');
      setShowMoodSetup(false);
    }
  }, [backendError, authorizationError, isAdminSignInFlow, showMoodSetup]);

  // Handle redirect processing - removing duplicate handling since App.tsx handles it
  useEffect(() => {
    // Only handle URL cleanup after authentication, not the redirect itself
    if (location.search.includes('code=') || location.search.includes('error=')) {
      authLogger.debug('Detected redirect parameters, processing...');
      setIsProcessingRedirect(true);
      // Wait longer for the redirect to be processed by App.tsx
      const timeout = setTimeout(() => {
        authLogger.debug('Redirect processing timeout reached, cleaning up URL');
        setIsProcessingRedirect(false);
        // Clean up URL parameters
        if (location.search.includes('code=') || location.search.includes('error=')) {
          navigate(location.pathname, { replace: true });
        }
      }, 3000); // Increased timeout to 3 seconds
      
      return () => clearTimeout(timeout);
    }

    // No redirect params present. This also covers the case where the OIDC/MSAL
    // service already cleaned the URL (via replaceState + popstate): the effect
    // re-runs with no code/error, so we must clear the processing flag here.
    // Otherwise isProcessingRedirect would stay true forever and block navigation
    // away from /auth/callback (leaving the user stuck on the spinner).
    setIsProcessingRedirect(false);
    return undefined;
  }, [location.search, location.pathname, navigate]);

  // Navigation logic
  useEffect(() => {
    const hasRedirectParams = location.search.includes('code=') || location.search.includes('error=');
    
    authLogger.debug('Navigation effect triggered:', {
      isLoading,
      isProcessingRedirect,
      isAuthenticated,
      msalInitialized,
      user: user ? { 
        id: user.id, 
        onboardingCompleted: user.hasCompletedOnboarding(),
        displayName: user.displayName 
      } : null,
      pathname: location.pathname,
      hasAccounts: accounts.length,
      hasRedirectParams,
      showMoodSetup,
      moodSetupCompleted,
      isAdminSignInFlow
    });

    // Native OIDC authentication lands on the /auth/callback route. Once the user
    // profile has been loaded, the auth redirect is fully complete, so navigate
    // into the app immediately. This MUST run before the loading / redirect-param
    // / mood-setup guards below: on the native Capacitor WebView the URL cleanup
    // (replaceState + synthetic popstate) does not reliably update React Router's
    // location, so the stale code/state params, the isProcessingRedirect flag, or
    // a pending mood modal would otherwise trap the user on /auth/callback behind
    // the login spinner forever. Any mood-setup modal overlays /chat just like on
    // web (where the OIDC redirect lands on the origin root, not /auth/callback).
    if (isAuthenticated && user && location.pathname === '/auth/callback') {
      if (!user.hasCompletedOnboarding() && user.consent && !isAdminSignInFlow) {
        authLogger.debug('Callback route authenticated - redirecting to onboarding');
        navigate('/onboarding');
      } else if (pendingAdminReturnUrl?.startsWith('/admin')) {
        authLogger.debug('Callback route authenticated - redirecting to admin return URL');
        navigate(pendingAdminReturnUrl);
      } else {
        authLogger.debug('Callback route authenticated - redirecting to chat');
        navigate('/chat');
      }
      return;
    }

    // Don't navigate while loading, processing redirect, or if auth provider isn't initialized yet
    // Demo mode is always "ready" since it doesn't require external initialization
    const providerReady = isDemoMode ||
                         (authProvider === 'msal' && msalInitialized) || 
                         (authProvider === 'oidc' && oidcInitialized) ||
                         msalInitialized; // Default to MSAL check

    if (isLoading || isProcessingRedirect || !providerReady) {
      authLogger.debug('Skipping navigation - loading, processing redirect, or auth provider not initialized');
      return;
    }

    // Don't navigate if we have redirect parameters - let the redirect complete first
    if (hasRedirectParams) {
      authLogger.debug('Skipping navigation - redirect parameters present');
      return;
    }

    // Don't navigate if mood setup modal is being shown
    if (showMoodSetup && !isAdminSignInFlow) {
      authLogger.debug('Skipping navigation - mood setup modal is shown');
      return;
    }

    if (isAuthenticated) {
      authLogger.debug('User is authenticated, checking navigation conditions...');
      
      // Check if we have account but no user profile yet
      // Demo mode doesn't have an external account - it uses the demo user ID
      const hasAccount = isDemoMode ||
                        (authProvider === 'oidc' && oidcAccount) || 
                        (authProvider === 'msal' && accounts.length > 0) ||
                        accounts.length > 0;
      
      if (hasAccount && !user) {
        authLogger.debug('Authenticated with account but user not loaded yet, waiting...');
        return;
      }
      
      // If authenticated but no account, something is wrong (except in demo mode)
      if (!hasAccount && !isDemoMode) {
        authLogger.warn('Authenticated but no account found, potential issue');
        return;
      }
      
      authLogger.debug('Evaluating user conditions:', {
        hasUser: !!user,
        onboardingCompleted: user?.hasCompletedOnboarding(),
        hasConsent: !!user?.consent,
        moodSetupCompleted,
        currentPath: location.pathname
      });
      
      // Redirect to onboarding if not completed AND user has consent configuration
      if (user && !user.hasCompletedOnboarding() && user.consent && location.pathname !== '/onboarding' && !isAdminSignInFlow) {
        authLogger.debug('Redirecting to onboarding - consent config exists');
        navigate('/onboarding');
      }
      // Skip onboarding if no consent configuration
      else if (user && !user.hasCompletedOnboarding() && !user.consent) {
        authLogger.debug('Skipping onboarding - no consent configuration in user profile');
        // User will go directly to chat or current route
      }
      // Redirect from /login (and the native /auth/callback) to chat (root / redirect is handled by RootRedirect component)
      else if (location.pathname === '/login' || location.pathname === '/auth/callback') {
        if (pendingAdminReturnUrl?.startsWith('/admin')) {
          authLogger.debug('Redirecting to admin return URL from login page');
          navigate(pendingAdminReturnUrl);
        } else {
          authLogger.debug('Redirecting to chat from login page');
          navigate('/chat');
        }
      }
    } else if (!isAuthenticated && location.pathname !== '/login' && location.pathname !== '/auth/callback' && !location.pathname.startsWith('/admin')) {
      // Only redirect to login if we're sure we're not authenticated
      // and not in the middle of processing a redirect
      // Admin routes handle their own authentication via AdminLoginPage
      authLogger.debug('Redirecting to login - not authenticated');
      navigate('/login');
    }
  }, [isAuthenticated, user, isLoading, isProcessingRedirect, msalInitialized, oidcInitialized, authProvider, location.pathname, location.search, navigate, accounts.length, oidcAccount, showMoodSetup, moodSetupCompleted, isDemoMode, isAdminSignInFlow, pendingAdminReturnUrl]);

  const signIn = async (provider: AuthProviderType = 'msal', returnUrl?: string): Promise<void> => {
    try {
      setAuthError(null);
      authLogger.debug('Initiating sign-in process with provider:', provider);
      if (returnUrl) {
        authLogger.debug('Return URL specified:', returnUrl);
      }
      
      // SECURITY: Clear demo mode when signing in with real credentials
      if (isDemoMode) {
        authLogger.debug('Clearing demo mode before real sign-in');
        demoService.deactivateDemoMode(false);
        setIsDemoMode(false);
        setUser(null);
        userService.clearCache();
      }
      
      setAuthProvider(provider);
      authProviderService.setActiveProvider(provider);

      if (provider === 'oidc') {
        // Store return URL for OIDC in localStorage (survives redirect)
        if (returnUrl) {
          localStorage.setItem('adminReturnUrl', returnUrl);
        }
        await oidcService.signIn();

        // On native (iOS) the OIDC flow completes in-app: oidcService.signIn() only
        // resolves after ASWebAuthenticationSession returns and the backend token
        // exchange succeeds (it throws otherwise). The web flow leaves the page via a
        // redirect, so this block is a no-op there. Set the auth state directly here so
        // it never depends on the window 'oidcUserLoaded' event being observed in time.
        if (isNativeApp()) {
          recordNativeDiagnostic('AuthContext native signIn resolved - setting auth state');
          setIsOidcAuthenticated(true);
          setAuthProvider('oidc');
          authProviderService.setActiveProvider('oidc');
          setOidcAccount(oidcService.getUserProfile());
        }
      } else {
        // Store return URL for MSAL in localStorage (MSAL state param doesn't work reliably)
        if (returnUrl) {
          localStorage.setItem('adminReturnUrl', returnUrl);
        }
        await instance.loginRedirect(loginRequest);
      }
    } catch (error) {
      authLogger.error('Sign in error:', error);
      
      // Check if this is a critical auth error that requires force reauthentication
      if (provider === 'msal' && msalService.shouldForceReauthentication(error)) {
        authLogger.warn('Critical auth error detected during sign-in, forcing reauthentication...');
        await msalService.forceReauthenticationPublic();
        return;
      }

      // Surface a detailed, user-visible reason instead of a generic message.
      const detail =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : (error as { message?: string })?.message;
      const providerLabel = provider === 'oidc' ? 'Renewmyday' : 'Microsoft';
      const detailedMessage = detail
        ? `Sign in with ${providerLabel} failed: ${detail}`
        : `Sign in with ${providerLabel} failed. Please try again.`;

      setAuthError(detailedMessage);
      setError(detailedMessage);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      // Handle demo mode sign out
      if (isDemoMode) {
        authLogger.debug('Signing out from demo mode');
        demoService.deactivateDemoMode(false); // Keep demo user data for next visit
        setIsDemoMode(false);
        setUser(null);
        setAuthError(null);
        setBackendError(null);
        userService.clearCache();
        navigate('/login');
        return;
      }

      // Determine actual provider - check both state and session storage for robustness
      const effectiveProvider = authProvider || 
        (sessionStorage.getItem('auth_provider') as 'msal' | 'oidc' | null) ||
        authProviderService.getActiveProvider();
      
      authLogger.debug('Initiating sign-out process for provider:', effectiveProvider);
      
      // Clear user-specific caches before logout to prevent cross-user data leakage
      userService.clearCache();
      authLogger.debug('User service cache cleared');
      
      // Clear React state first
      setUser(null);
      setAuthError(null);
      setBackendError(null);
      setAuthProvider(null);
      
      // Clear provider state
      authProviderService.clearActiveProvider();
      
      if (effectiveProvider === 'oidc') {
        // OIDC handles its own cleanup and redirect
        setIsOidcAuthenticated(false);
        setOidcAccount(null);
        await oidcService.signOut();
        // Note: oidcService.signOut() redirects to /login, so we won't reach here
      } else {
        // MSAL logout - this will redirect, so cleanup above happens first
        await instance.logoutRedirect({
          postLogoutRedirectUri: "/"
        });
      }
    } catch (error) {
      authLogger.error('Sign out error:', error);
      
      // Ensure state is cleared even on error
      setUser(null);
      setAuthProvider(null);
      setIsOidcAuthenticated(false);
      setOidcAccount(null);
      authProviderService.clearActiveProvider();
      
      setAuthError('Sign out failed');
      setError('Sign out failed');
      throw error;
    }
  };

  const getAccessToken = async (scopes: string[] = loginRequest.scopes): Promise<string | null> => {
    // In demo mode, return a placeholder - the actual SignalR token comes from negotiate endpoint
    if (isDemoMode) {
      authLogger.debug('Demo mode: returning placeholder token (actual token from negotiate)');
      return 'demo-mode-placeholder';
    }

    // Use OIDC token if authenticated via OIDC
    if (authProvider === 'oidc') {
      try {
        const token = await oidcService.getAccessToken();
        if (token) {
          authLogger.debug('OIDC token acquired successfully');
          return token;
        }
        authLogger.debug('No OIDC token available');
        return null;
      } catch (error) {
        authLogger.error('OIDC token acquisition error:', error);
        return null;
      }
    }

    // Default to MSAL for Azure AD authentication
    if (!account) {
      authLogger.debug('No account available for token acquisition');
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes,
        account
      });
      authLogger.debug('Token acquired successfully');
      return response.accessToken;
    } catch (error) {
      authLogger.error('Token acquisition error:', error);
      return null;
    }
  };

  const clearErrors = (): void => {
    authLogger.debug('Clearing authentication errors');
    setAuthError(null);
    setBackendError(null);
    setError(null);
  };

  const forceReauthentication = async (): Promise<void> => {
    authLogger.warn('Force reauthentication requested from AuthContext');
    await msalService.forceReauthenticationPublic();
  };

  const immediateForceLogin = async (): Promise<void> => {
    authLogger.warn('Immediate force login requested from AuthContext');
    await msalService.immediateForceLogin();
  };

  const refreshUser = async (): Promise<void> => {
    // Handle demo mode separately - no MSAL/OIDC account available
    if (isDemoMode) {
      try {
        authLogger.debug('Refreshing demo user profile (forcing cache bypass)');
        setIsLoading(true);
        const userProfile = await userService.getProfile(true);
        if (userProfile) {
          const demoUserInfo = demoService.getDemoUserInfo();
          // For demo users, use backend profile data but ensure demo user ID
          const refreshedUser = new User({
            ...userProfile,
            id: demoUserInfo.id, // Ensure we keep the demo user ID
          });
          setUser(refreshedUser);
          authLogger.debug('Demo user profile refreshed successfully');
        }
      } catch (error) {
        authLogger.error('Failed to refresh demo user profile:', error);
        setBackendError('Failed to refresh user profile');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Regular MSAL/OIDC authentication flow
    if (!isAuthenticated || !account) {
      authLogger.debug('Cannot refresh user - not authenticated or no account');
      return;
    }

    try {
      authLogger.debug('Refreshing user profile (forcing cache bypass)');
      setIsLoading(true);
      // Force refresh to bypass cache and get fresh data from backend
      const userProfile = await userService.getProfile(true);
      if (userProfile) {
        const mergedUser = User.fromAuthContext(account, userProfile);
        setUser(mergedUser);
        authLogger.debug('User profile refreshed successfully');
      }
    } catch (error) {
      authLogger.error('Failed to refresh user profile:', error);
      setBackendError('Failed to refresh user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMoodSetupComplete = (): void => {
    authLogger.info('Mood setup completed');
    setShowMoodSetup(false);
    setMoodSetupCompleted(true);
    // Navigate to chat after mood setup is complete unless the user is entering admin.
    if (!isAdminSignInFlow) {
      navigate('/chat');
    }
  };

  const switchAccount = async (): Promise<void> => {
    // Determine actual provider - check both state and session storage for robustness
    const effectiveProvider = authProvider || 
      (sessionStorage.getItem('auth_provider') as 'msal' | 'oidc' | null) ||
      authProviderService.getActiveProvider();
    
    try {
      authLogger.info('User switching accounts', {
        currentAccount: account?.username || account?.email,
        provider: effectiveProvider
      });

      // Clear errors and user state
      setAuthorizationError(null);
      setAuthError(null);
      setBackendError(null);
      setUser(null);

      // Clear user-specific caches to prevent cross-user data leakage
      userService.clearCache();
      authLogger.debug('User service cache cleared for account switch');

      if (effectiveProvider === 'oidc') {
        // OIDC account switch - clears local state and redirects to login
        setIsOidcAuthenticated(false);
        setOidcAccount(null);
        setAuthProvider(null);
        authProviderService.clearActiveProvider();
        await oidcService.switchAccount();
        // Note: oidcService.switchAccount() redirects, so we won't reach here
      } else {
        // MSAL account switch - force account picker on next login
        await instance.logoutRedirect({
          account: account || undefined,
          postLogoutRedirectUri: '/login?prompt=select_account'
        });
      }
    } catch (error) {
      authLogger.error('Account switch failed:', error);
      throw error;
    }
  };

  const signInDemo = async (): Promise<void> => {
    if (!isDemoModeAvailable) {
      authLogger.warn('Demo mode is not available');
      setAuthError('Demo mode is not available');
      return;
    }

    try {
      authLogger.info('Checking demo credentials');
      
      // Check if we have a stored password and validate it
      if (demoService.hasDemoPassword()) {
        authLogger.debug('Found stored demo password, validating...');
        const isValid = await demoService.validateStoredPassword();
        
        if (isValid) {
          authLogger.info('Stored demo password is valid, activating demo mode');
          demoService.activateDemoMode();
          setIsDemoMode(true);
          setAuthError(null);
          setBackendError(null);
          setNeedsDemoPassword(false);
          navigate('/chat');
          return;
        }
        
        // Password was invalid (rotated), need to ask for new one
        authLogger.warn('Stored demo password is invalid, requesting new password');
      }
      
      // No valid password stored, show password modal
      authLogger.info('Demo password required');
      setNeedsDemoPassword(true);
    } catch (error) {
      authLogger.error('Demo mode check failed:', error);
      setAuthError('Failed to check demo credentials');
      setNeedsDemoPassword(true);
    }
  };

  const signInDemoWithPassword = async (password: string): Promise<boolean> => {
    if (!isDemoModeAvailable) {
      authLogger.warn('Demo mode is not available');
      setAuthError('Demo mode is not available');
      return false;
    }

    try {
      authLogger.info('Validating demo password');
      
      const isValid = await demoService.validateDemoPassword(password);
      
      if (!isValid) {
        authLogger.warn('Demo password validation failed');
        return false;
      }
      
      // Password is valid, store it and activate demo mode
      authLogger.info('Demo password validated, activating demo mode');
      demoService.setDemoPassword(password);
      demoService.activateDemoMode();
      setIsDemoMode(true);
      setAuthError(null);
      setBackendError(null);
      setNeedsDemoPassword(false);
      navigate('/chat');
      return true;
    } catch (error) {
      authLogger.error('Demo password validation failed:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    error: error || undefined,
    account,
    isProcessingRedirect,
    authError,
    backendError,
    login: signIn,
    logout: signOut,
    signIn,
    signOut,
    getAccessToken,
    clearErrors,
    refreshUser,
    forceReauthentication,
    immediateForceLogin,
    switchAccount,
    authorizationError,
    authProvider,
    isOidcAvailable: OIDC_ENABLED,
    // Demo mode
    isDemoMode,
    isDemoModeAvailable,
    signInDemo,
    signInDemoWithPassword,
    needsDemoPassword,
    setNeedsDemoPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <MoodSetupModal isOpen={showMoodSetup} onComplete={handleMoodSetupComplete} />
    </AuthContext.Provider>
  );
};
